<template>
    <div class="full-modal">
        <div class="btn-exit grow-medium" @click="ExitModal"><i class="pi pi-times"></i></div>
        <h1 class="top-text">{{ planTypeString }} Plan Details</h1>
        
        <div class="left-row" style="margin-bottom: 0em;">
            <span class="breakage-text">Breakage Discount: &nbsp;
                <div class="info-icon" @mouseover="showBreakageTooltip = true" @mouseleave="showBreakageTooltip = false">
                    <i class="pi pi-question-circle"></i>
                    <transition name="fade">
                        <div v-if="showBreakageTooltip" class="tooltip">
                            Breakage is extra revenue from members who don't come in right when they're eligible for their benefits. This can create funds that can be used for rewards, additional services or profitability for your plan. Alternately, a higher breakage assumption allows you to lower a member's monthly cost while reimbursing yourself the same amount for treatments. Average breakage is 15%.
                        </div>
                    </transition>
                </div> 
            </span>
            <div class="checkbox-container">
                <div class="plus-minus-buttons">
                    <PlusMinusButton class="plus-minus" :char="'+'" @plusMinusClick="UpdatePercentBreakage"/>
                    <PlusMinusButton class="plus-minus" :char="'-'" @plusMinusClick="UpdatePercentBreakage"/>
                </div>
                <div class="breakage-box no-select">{{ percentBreakage }}<span style="font-size: 0.65em;">%</span></div>
            </div>
        </div>
        
        <div class="left-row">
            <span class="breakage-text">Include Dental Menu Fee:</span>
            <div class="checkbox-container grow-medium">
                <div class="checkbox" @click="toggleDMFee">
                    <div class="checkbox-fill" v-if="includeDMFee">
                        <p class="checkbox-text">✓</p>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="left-row">
            <span class="breakage-text">Include Processing Fee:</span>
            <div class="checkbox-container grow-medium">
                <div class="checkbox" @click="toggleProcessingFee">
                    <div class="checkbox-fill" v-if="includeProcessingFee">
                        <p class="checkbox-text">✓</p>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="left-row">
            <span class="breakage-text">Set All Benefits to:</span>
            <div class="checkbox-container">
                <input class="percent-input hover-grow" v-model="localPercentToSetTo" @input="handlePercentInput"/><span class="percent-sign">% UCR</span>
            </div>
        </div>
        
        <div class="left-row">
            <span class="breakage-text">Total Plan Discount:</span>
            <div class="checkbox-container">
                <div class="percent-input" style="border: 0px; margin-right: 0;">{{ percentDiscount }}</div><span class="percent-sign">% off UCR</span>
            </div>
        </div>
        
        <!-- Price Change Confirmation Modal -->
        <ChangePriceAlertModal
            :show="showPriceChangeConfirmation"
            :benefit=null
            @cancel="cancelPercentChange"
            @confirm="handlePercentChangeConfirmed"
        />
    </div>
</template>

<script>
import PlusMinusButton from '../PlusMinusButton.vue';
import ChangePriceAlertModal from './ChangePriceAlertModal.vue';
import { InputToInteger } from '../../utils/functions';

export default {
    components: {
        PlusMinusButton,
        ChangePriceAlertModal
    },
    props: {
        planType: Number,
        planTypeString: String,
        percentBreakage: Number,
        includeDMFee: Boolean,
        includeProcessingFee: Boolean,
        percentToSetTo: Number,
        percentDiscount: Number,
    },
    data() {
        return {
            showBreakageTooltip: false,
            localPercentToSetTo: this.percentToSetTo,
            showPriceChangeConfirmation: false,
            attemptedPercentValue: null,
            originalPercentValue: this.percentToSetTo,
        }
    },
    methods: {
        ExitModal() {
            this.$emit('exitModal');
        },
        UpdatePercentBreakage(value) {
            this.$emit('updatePercentBreakage', value);
        },
        toggleDMFee() {
            this.$emit('toggleDMFee');
        },
        toggleProcessingFee() {
            this.$emit('toggleProcessingFee');
        },
        handlePercentInput(event) {
            let result = InputToInteger(event.target.value);
            if(result < 101) {
                this.localPercentToSetTo = result;
            }
            else {
                this.localPercentToSetTo = InputToInteger(event.target.value.slice(0, -1));
            }
            
            // Check if value changed and lock is active
            if (this.localPercentToSetTo !== this.originalPercentValue && 
                this.hasMultiPlanBenefits && 
                !this.$store.state.allowMultiPlanPriceChanges) {
                // Store the attempted value and show modal
                this.attemptedPercentValue = this.localPercentToSetTo;
                this.showPriceChangeConfirmation = true;
                // Revert the input to the original value
                this.$nextTick(() => {
                    this.localPercentToSetTo = this.originalPercentValue;
                });
                return;
            }
            
            // If lock is not active or already cleared, allow the change
            this.originalPercentValue = this.localPercentToSetTo;
            this.$emit('updatePercentToSetTo', this.localPercentToSetTo);
        },
        handlePercentChangeConfirmed() {
            // The modal has already set the session-wide flag in the store
            // Apply the attempted value
            if (this.attemptedPercentValue !== null) {
                this.localPercentToSetTo = this.attemptedPercentValue;
                this.originalPercentValue = this.attemptedPercentValue;
                this.$emit('updatePercentToSetTo', this.attemptedPercentValue);
                this.attemptedPercentValue = null;
            }
            this.showPriceChangeConfirmation = false;
        },
        cancelPercentChange() {
            // Revert to original value if user cancels
            if (this.attemptedPercentValue !== null) {
                this.localPercentToSetTo = this.originalPercentValue;
                this.attemptedPercentValue = null;
            }
            this.showPriceChangeConfirmation = false;
        },
        handleEscape(event) {
            if(event.key === 'Escape') {
                if(this.showPriceChangeConfirmation) {
                    this.cancelPercentChange();
                } else {
                    this.ExitModal();
                }
            }
        },
    },
    computed: {
        hasMultiPlanBenefits() {
            // Check if any benefits in this plan type are on multiple plans
            const benefits = this.$store.getters.benefitsByType(this.planType);
            return benefits.some(benefit => benefit.planTypes && benefit.planTypes.length > 1);
        },
    },
    watch: {
        percentToSetTo(newVal) {
            this.localPercentToSetTo = newVal;
            this.originalPercentValue = newVal;
        }
    },
    mounted() {
        window.addEventListener('keydown', this.handleEscape);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleEscape);
    },
}
</script>

<style scoped>
.full-modal {
    position: relative;
    width: 30%;
    min-width: 280px;
    max-height: 90vh;
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 20px;
    border: 3px solid var(--color-dark-blue);
    outline: 2px solid white;
    padding: 2%;
    overflow-y: auto;
    box-sizing: border-box;
}

.btn-exit {
    position: absolute;
    top: 20px;
    right: 25px;
    color: red;
    font-size: 1.75em;
    cursor: pointer;
    font-family: sans-serif;
}

.top-text {
    color: var(--font-color-teal);
    margin: 0.5em 0;
    flex-shrink: 0;
}

.left-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.7em;
    flex-shrink: 0;
}

.breakage-text {
    display: flex;
    flex: 3;
    font-size: 1.2em;
}

.info-icon {
    color: var(--color-medium-gray);
    padding-top: 2px;
    position: relative;
}

.tooltip {
    position: absolute;
    top: -165px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #fff;
    color: #333;
    padding: 5px 10px;
    border-radius: 8px;
    border: 2px solid var(--color-light-blue);
    font-size: 0.75em;
    z-index: 9;
    text-wrap: wrap;
    width: 2000%;
}

.checkbox-container {
    position: relative;
    display: flex;
    flex: 2;
    justify-content: center;
    align-items: center;
}

.checkbox {
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid var(--color-dark-blue);
    border-radius: 5px;
    cursor: pointer;
}

.checkbox-fill {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    background-color: var(--color-dark-blue);
    border-radius: 3px;
    color: white;
}

.checkbox-text {
    margin: 0;
    font-size: 1.5em;
    left: 2px;
    top: -3px;
}

.breakage-box {
    font-size: 1.7em;
    color: var(--color-bright-blue);
    background-color: white;
    border: 2px solid var(--color-light-blue);
    border-radius: 10px;
    margin: 0.5em;
    margin-right: 0.35em;
    padding: 0.1em;
    min-width: 45px;
    text-align: right;
    font-weight: bold;
}

.percent-input {
    font-size: 1.7em;
    font-weight: 900;
    color: var(--color-bright-blue);
    border: 2px solid var(--color-light-blue);
    border-radius: 10px;
    margin-right: 0.35em;
    padding: 0.1em;
    max-width: 50px;
    text-align: center;
}

.percent-sign {
    font-size: 1.15em;
    color: var(--color-bright-blue);
    font-weight: 900;
}
</style>

